import { useEffect, useRef, useState } from "react";
import {
  BACK_END_API_URL,
  COGNITO_DOMAIN,
  FRONT_END_DOMAIN,
  USER_POOL_CLIENT_ID,
  VERIFICATION_METHOD,
} from "../../utils/LS-CONSTANT";
import { usePermissions } from "../../provider/PermissionProvider";
import sessionManager from '../session/SessionManager';
import SessionWarningModal from "../session/SessionWarningModal";
import { LsButton } from "../../core";
import LsPageLoader from "../../core/LsPageLoader";

const AuthGate = ({ children, loadingComponent = null }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { reloadPermissions } = usePermissions();
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const hasAttemptedReload = useRef(false);

  const handleContinueSession = () => {
    setShowSessionWarning(false);
    sessionManager.continueSession();
  };

  const handleLogoutNow = () => {
    setShowSessionWarning(false);
    sessionManager.performLogout();
  };

  const COGNITO_LOGIN_URL =
    `https://${COGNITO_DOMAIN}/oauth2/authorize` +
    `?response_type=${VERIFICATION_METHOD}` +
    `&client_id=${USER_POOL_CLIENT_ID}` +
    `&redirect_uri=https://${FRONT_END_DOMAIN}`;

  const storeTokensInCookies = (tokens) => {
    const expires = new Date(Date.now() + 3600 * 1000).toUTCString(); // 1 hour expiry
    document.cookie = `access_token=${tokens.access_token}; expires=${expires}; path=/; Secure`;
    document.cookie = `id_token=${tokens.id_token}; expires=${expires}; path=/; Secure`;
    document.cookie = `refresh_token=${tokens.refresh_token}; expires=${expires}; path=/; Secure`;
  };

  const exchangeCodeForToken = async (code, redirectUri) => {
    try {
      const response = await fetch(
        `${BACK_END_API_URL}/api/auth/token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code,
            redirectUri,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Token exchange failed: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error("Error exchanging code:", err);
      throw err;
    }
  };

  const getCookie = (name) => {
    const match = document.cookie.match(
      new RegExp("(^| )" + name + "=([^;]+)")
    );
    return match ? match[2] : null;
  };

  const loadPermissionsAfterAuth = async () => {
    if (hasAttemptedReload.current) return;

    hasAttemptedReload.current = true;
    console.log("Loading permissions after auth...");

    try {
      await reloadPermissions();
      console.log("Permissions loaded successfully");
      setPermissionsLoaded(true);
    } catch (error) {
      console.error("Failed to load permissions, retrying...", error);
      // Retry once after a delay
      setTimeout(async () => {
        try {
          await reloadPermissions();
          setPermissionsLoaded(true);
        } catch (retryError) {
          console.error("Failed to load permissions on retry", retryError);
          setPermissionsLoaded(true); // Still set to true to avoid blocking UI
        }
      }, 1000);
    }
  };

  const checkAuthStatus = async () => {
    // 1. Check Duo token in cookies
    const existingCookieToken = getCookie("access_token");

    // 2. Check Cognito token in localStorage
    const cognitoTokenKey = Object.keys(localStorage).find(
      (key) =>
        key.includes("CognitoIdentityServiceProvider") &&
        key.endsWith(".accessToken")
    );
    const existingCognitoToken = cognitoTokenKey
      ? localStorage.getItem(cognitoTokenKey)
      : null;

    if (existingCookieToken || existingCognitoToken) {
      console.log("User is authenticated with existing token");
      setIsAuthenticated(true);

      // START SESSION MANAGER
      sessionManager.start(
        FRONT_END_DOMAIN,
        () => setShowSessionWarning(true)
      );

      // Load permissions after auth is confirmed
      await loadPermissionsAfterAuth();
      return;
    }

    // 3. Handle Duo OAuth code exchange
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code) {
      try {
        console.log("Exchanging code for token...");
        const tokens = await exchangeCodeForToken(
          code,
          `https://${FRONT_END_DOMAIN}`
        );
        storeTokensInCookies(tokens);
        console.log("Token exchange successful");
        setIsAuthenticated(true);

        // START SESSION MANAGER AFTER SUCCESSFUL LOGIN
        sessionManager.start(
          FRONT_END_DOMAIN,
          () => setShowSessionWarning(true)
        );

        // Load permissions after auth is confirmed
        await loadPermissionsAfterAuth();

        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        ); // Clean URL
      } catch (err) {
        console.error("Token exchange failed:", err);
        window.location.href = COGNITO_LOGIN_URL;
      }
    } else {
      console.log("No token or code found, redirecting to login...");
      window.location.href = COGNITO_LOGIN_URL;
    }
  };

  useEffect(() => {
    const init = async () => {
      await checkAuthStatus();
    };

    init();

    return () => {
      sessionManager.stop();
    };
  }, []);

  // Effect to update loading state when both auth and permissions are ready
  useEffect(() => {
    if (isAuthenticated && permissionsLoaded) {
      console.log("Auth and permissions ready, stopping loader");
      setIsLoading(false);
    }
  }, [isAuthenticated, permissionsLoaded]);

  // Effect to retry permissions if not loaded initially
  useEffect(() => {
    if (isAuthenticated && !permissionsLoaded && !isLoading) {
      const timeoutId = setTimeout(() => {
        console.log("Retrying permissions load...");
        loadPermissionsAfterAuth();
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated, permissionsLoaded, isLoading]);

  // Debug logging
  useEffect(() => {
    console.log("AuthGate state:", {
      isAuthenticated,
      permissionsLoaded,
      isLoading,
      hasAttemptedReload: hasAttemptedReload.current
    });
  }, [isAuthenticated, permissionsLoaded, isLoading]);

  if (isLoading) {
    console.log("Showing loader...");
    return loadingComponent || <LsPageLoader fullScreen message="Please wait while we redirect..." />;
  }

  // Show error state
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          padding: "20px",
        }}
      >
        <div style={{ color: "red", marginBottom: "20px" }}>{error}</div>
        <LsButton onClick={() => (window.location.href = COGNITO_LOGIN_URL)}>
          Try Again
        </LsButton>
      </div>
    );
  }

  // Only render children when authenticated AND permissions are loaded
  console.log("Rendering children, auth state:", { isAuthenticated, permissionsLoaded });
  return isAuthenticated && permissionsLoaded ? (
    <>
      <SessionWarningModal
        open={showSessionWarning}
        onContinue={handleContinueSession}
        onLogout={handleLogoutNow}
      />
      {children}
    </>
  ) : null;
};

export default AuthGate;